import { useState, useCallback, useMemo } from 'react';
import { correctEmail } from '@ajat/design-system/utils/emailCorrection';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TOPICS = ['order', 'design', 'delivery', 'payment', 'school', 'other'];

/**
 * Shared Contact-form hook.
 *
 * @param {Object} config
 * @param {Object} config.labels   - Localized error strings
 * @param {Function} config.onSubmit - async ({ data }) => void
 */
export default function useContact({ labels = {}, onSubmit }) {
  const initial = useMemo(
    // `company` is a honeypot — kept empty by real users (hidden field); bots
    // that auto-fill it are dropped server-side. Not validated.
    () => ({ name: '', email: '', topic: '', message: '', company: '', captchaToken: '' }),
    [],
  );

  const [data, setData] = useState(initial);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const update = useCallback((field) => (eOrValue) => {
    const value = eOrValue && eOrValue.target ? eOrValue.target.value : eOrValue;
    setData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setField = useCallback((field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const validators = useMemo(() => ({
    name: (v) => (!v?.trim() ? (labels.required || 'Required') : ''),
    email: (v) => {
      if (!v?.trim()) return labels.required || 'Required';
      return EMAIL_RE.test(v) ? '' : (labels.invalidEmail || 'Invalid email');
    },
    topic: (v) => (!v ? (labels.required || 'Required') : ''),
    message: (v) => (!v?.trim() ? (labels.required || 'Required') : ''),
  }), [labels]);

  const isFieldValid = useCallback((field) => !validators[field]?.(data[field]), [validators, data]);
  const fieldError = useCallback((field) => {
    if (!touched[field]) return '';
    return validators[field]?.(data[field]) || '';
  }, [validators, data, touched]);

  const handleBlur = useCallback((field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === 'email') {
      setData((prev) => {
        if (!prev.email) return prev;
        const fixed = correctEmail(prev.email);
        return fixed !== prev.email ? { ...prev, email: fixed } : prev;
      });
    }
  }, []);

  const handleFocus = useCallback((field) => () => {
    setTouched((prev) => ({ ...prev, [field]: false }));
  }, []);

  const validateAll = useCallback(() => {
    const errors = {};
    Object.keys(validators).forEach((f) => {
      const err = validators[f](data[f]);
      if (err) errors[f] = err;
    });
    setTouched(Object.keys(validators).reduce((acc, f) => ({ ...acc, [f]: true }), {}));
    return Object.keys(errors).length === 0;
  }, [validators, data]);

  const handleSubmit = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitting) return;
    setSubmitError('');
    if (!validateAll()) return;
    setSubmitting(true);
    try {
      if (onSubmit) await onSubmit({ data });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.message || labels.submitError || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }, [submitting, validateAll, onSubmit, data, labels.submitError]);

  const reset = useCallback(() => {
    setData(initial);
    setTouched({});
    setSubmitted(false);
    setSubmitError('');
  }, [initial]);

  return {
    data,
    update,
    setField,
    touched,
    handleBlur,
    handleFocus,
    isFieldValid,
    fieldError,
    validateAll,
    handleSubmit,
    submitting,
    submitted,
    submitError,
    reset,
    TOPICS,
  };
}
